import { ref } from 'vue'
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  where
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

/**
 * Hot Properties (인기 현장) Composable
 *
 * - 상담 신청 수 기준으로 인기 현장 자동 정렬
 * - 첫 번째 슬롯은 AD (Admin이 캠페인 연결)
 *
 * Firestore 컬렉션:
 * - properties: consultationCount 필드로 정렬
 * - hotAd: 단일 문서, 첫 번째 AD 슬롯 관리
 */
export function useHotProperty() {
  const loading = ref(false)
  const error = ref(null)

  /**
   * 현장별 찜 수 조회
   * @param {string} propertyId - 현장 ID
   */
  const getFavoriteCount = async (propertyId) => {
    try {
      const q = query(
        collection(db, 'favorites'),
        where('propertyId', '==', propertyId)
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch {
      return 0
    }
  }

  /**
   * 인기 현장 목록 조회 (상담 신청 수 기준)
   * @param {number} limitCount - 가져올 개수 (기본 5개)
   */
  const getHotProperties = async (limitCount = 5) => {
    loading.value = true
    error.value = null

    try {
      // consultationCount 필드가 없는 문서도 있을 수 있으므로
      // 먼저 전체를 가져온 후 클라이언트에서 정렬
      const q = query(
        collection(db, 'properties'),
        orderBy('createdAt', 'desc'),
        limit(20) // 충분히 가져온 후 정렬
      )
      const snapshot = await getDocs(q)
      const properties = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // 각 현장별 찜 수 조회
      const propertiesWithLikes = await Promise.all(
        properties.map(async (property) => {
          const likeCount = await getFavoriteCount(property.id)
          return { ...property, likeCount }
        })
      )

      // likeCount(찜 수) 기준 내림차순 정렬
      propertiesWithLikes.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))

      return propertiesWithLikes.slice(0, limitCount)
    } catch (err) {
      console.error('인기 현장 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  /**
   * Hot AD 슬롯 정보 조회 (Admin이 설정한 광고)
   */
  const getHotAd = async () => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'hotAd', 'current')
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        // 만료 확인
        if (data.expiresAt && new Date(data.expiresAt) < new Date()) {
          return null
        }
        return { id: docSnap.id, ...data }
      }
      return null
    } catch (err) {
      console.error('Hot AD 조회 실패:', err)
      error.value = err.message
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Hot AD 슬롯 설정 (Admin 전용)
   * @param {Object} adData - 광고 데이터
   */
  const setHotAd = async (adData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'hotAd', 'current')
      await setDoc(docRef, {
        ...adData,
        updatedAt: serverTimestamp()
      })
      return true
    } catch (err) {
      console.error('Hot AD 설정 실패:', err)
      error.value = err.message
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Hot AD 슬롯 업데이트 (Admin 전용)
   */
  const updateHotAd = async (updateData) => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'hotAd', 'current')
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp()
      })
      return true
    } catch (err) {
      console.error('Hot AD 업데이트 실패:', err)
      error.value = err.message
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Hot AD 슬롯 삭제 (Admin 전용)
   */
  const deleteHotAd = async () => {
    loading.value = true
    error.value = null

    try {
      const docRef = doc(db, 'hotAd', 'current')
      await deleteDoc(docRef)
      return true
    } catch (err) {
      console.error('Hot AD 삭제 실패:', err)
      error.value = err.message
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 활성 캠페인 목록 조회 (Admin 전용 - Hot AD 연결용)
   */
  const getActiveCampaigns = async () => {
    loading.value = true
    error.value = null

    try {
      const q = query(
        collection(db, 'campaigns'),
        where('status', '==', 'active'),
        orderBy('createdAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('활성 캠페인 조회 실패:', err)
      error.value = err.message
      return []
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    error,
    getHotProperties,
    getHotAd,
    setHotAd,
    updateHotAd,
    deleteHotAd,
    getActiveCampaigns
  }
}
